import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from './SafeIcon'

const { FiSearch, FiCamera, FiX } = FiIcons

function SearchBar() {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const inputRef = useRef(null)

  // Get search query from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlQuery = searchParams.get('q')
    if (urlQuery) {
      setQuery(urlQuery)
    }
  }, [location.search])

  const [recentSearches, setRecentSearches] = useState([])

  useEffect(() => {
    const saved = localStorage.getItem('recentSearches')
    if (saved) {
      setRecentSearches(JSON.parse(saved))
    }
  }, [])

  const handleSearch = (searchQuery = query) => {
    if (searchQuery.trim()) {
      const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5)
      setRecentSearches(updated)
      localStorage.setItem('recentSearches', JSON.stringify(updated))
      
      const searchParams = new URLSearchParams()
      searchParams.set('q', searchQuery)
      navigate(`/search?${searchParams.toString()}`)
      setIsOpen(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const clearSearch = () => {
    setQuery('')
    inputRef.current?.focus()
  }

  const popularCategories = [
    'Smartphone', 'Laptop', 'T-Shirt', 'Shoes', 'Watch', 
    'Bag', 'Headphones', 'Camera', 'Drone', 'Furniture'
  ]

  return (
    <div className="relative w-full">
      <div className="flex border-2 border-[#FF6600] rounded-lg overflow-hidden bg-white">
        {/* Search Input */}
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyPress}
            placeholder={t('search.placeholder')}
            className="w-full h-12 px-4 text-sm focus:outline-none"
          />
          {/* Clear button */}
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-14 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Button */}
        <button
          onClick={() => handleSearch()}
          className="bg-[#FF6600] text-white px-6 font-medium text-sm hover:bg-[#e55c00] transition-colors flex items-center"
        >
          <SafeIcon icon={FiSearch} className="w-5 h-5" />
          <span className="ml-2 hidden sm:inline">{t('search.button')}</span>
        </button>
      </div>

      {/* Search Suggestions */}
      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto"
            >
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                    {t('search.recent')}
                  </h3>
                  <div className="space-y-1">
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleSearch(search)}
                        className="w-full text-left px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded flex items-center"
                      >
                        <SafeIcon icon={FiSearch} className="w-4 h-4 mr-2 text-gray-400" />
                        {search}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Categories */}
              <div className="p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                  {t('search.popularSearches')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularCategories.map((category, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearch(category)}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="p-4 border-t border-gray-100 bg-gray-50">
                <div className="text-xs text-gray-500">
                  {t('common.pressEnterToSearch') || 'Press Enter to search or use advanced filters'}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SearchBar